<script setup>
import { InitViewIDs } from '@src/config';
import { computed, ref } from 'vue';

import { useViewportStateStore } from '../store/contours/viewport-state-store';
import { useCurrentStudyStore } from '../store/dicom-web/current-study-store';

const props = defineProps({
  seriesIdx: { type: Number, default: -1 },
});

const emit = defineEmits(['update:seriesIdx', 'hide']);

function hideOverlay() {
  emit('hide');
}

const currentStudyStore = useCurrentStudyStore();
const viewportStateStore = useViewportStateStore();

const isDragOver = ref(false);
const errorMessage = ref('');
const selectedSeriesIdx = ref(props.seriesIdx);

const hasValidSelection = computed(() => selectedSeriesIdx.value >= 0);

const thumbnailUrl = computed(() => {
  if (selectedSeriesIdx.value < 0) return null;
  return currentStudyStore.getSeriesThumbnails[selectedSeriesIdx.value] ?? null;
});

const seriesDims = computed(() => {
  if (selectedSeriesIdx.value < 0) return null;
  return currentStudyStore.getSeriesDimensions[selectedSeriesIdx.value] ?? null;
});

const sliceCount = computed(() => seriesDims.value?.sliceCount ?? 0);
const phaseCount = computed(() => seriesDims.value?.phaseCount ?? 0);

const seriesDescription = computed(() => {
  if (selectedSeriesIdx.value < 0) return '';
  const descriptions = currentStudyStore.getSeriesThumbnailDescriptions;
  return descriptions[selectedSeriesIdx.value] ?? '';
});

function validatePhantomSeries(seriesIdx) {
  const flowIndices = viewportStateStore.getSelectedImageIndices(
    InitViewIDs.FlowPhase,
  );
  if (flowIndices.seriesIdx < 0) return 'No flow series loaded';

  //   if (seriesIdx === flowIndices.seriesIdx)
  //     return 'Cannot use same series as flow';

  const series = currentStudyStore.activeStudy.series[seriesIdx];
  if (!series) return 'Series not found';

  const flowSeries
    = currentStudyStore.activeStudy.series[flowIndices.seriesIdx];
  if (!flowSeries) return 'Flow series not found';

  if (!series.slices || series.slices.length < 2)
    return 'Need >= 2 slices (phase+mag)';

  const phantomPhaseCount = series.slices[0]?.images?.length ?? 0;
  const flowPhaseCount = flowSeries.slices[0]?.images?.length ?? 0;
  if (phantomPhaseCount !== flowPhaseCount) {
    return `Phase count mismatch (${phantomPhaseCount} vs ${flowPhaseCount})`;
  }

  return null; // valid
}

function onDrop(event) {
  isDragOver.value = false;
  errorMessage.value = '';

  if (!event.dataTransfer) return;
  const raw = event.dataTransfer.getData('ImageIndices');
  if (!raw) return;

  const indices = JSON.parse(raw);
  const validationError = validatePhantomSeries(indices.seriesIdx);

  if (validationError) {
    errorMessage.value = validationError;
    selectedSeriesIdx.value = -1;
    emit('update:seriesIdx', -1);
    return;
  }

  selectedSeriesIdx.value = indices.seriesIdx;
  errorMessage.value = '';
  emit('update:seriesIdx', indices.seriesIdx);
}

function onDragOver() {
  isDragOver.value = true;
}

function onDragEnter() {
  isDragOver.value = true;
}

function onDragLeave() {
  isDragOver.value = false;
}

// function clearSelection() {
//   selectedSeriesIdx.value = -1;
//   errorMessage.value = '';
//   emit('update:seriesIdx', -1);
// }
</script>

<template>
  <div
    class="phantom-series-slot"
    :class="{
      'drag-over': isDragOver,
      'has-selection': hasValidSelection,
      'has-error': !!errorMessage,
    }"
    @drop.prevent="onDrop"
    @dragover.prevent="onDragOver"
    @dragenter.prevent="onDragEnter"
    @dragleave="onDragLeave"
  >
    <!-- Valid selection -->
    <template v-if="hasValidSelection">
      <div class="phantom-thumbnail">
        <img
          v-if="thumbnailUrl"
          :src="thumbnailUrl"
          alt="Phantom series"
          class="thumb-img"
        >
        <div class="thumb-info">
          <span class="series-idx">{{ selectedSeriesIdx + 1 }}</span>
          <span class="dims">{{ sliceCount }}/{{ phaseCount }}</span>
        </div>
      </div>
      <button class="clear-btn" title="Hide" @click="hideOverlay">
        <i class="mdi mdi-chevron-down" />
      </button>
      <v-tooltip activator="parent" location="top">
        {{ seriesDescription || `Series ${selectedSeriesIdx + 1}` }}
        ({{ sliceCount }}/{{ phaseCount }})
      </v-tooltip>
    </template>

    <!-- Error state -->
    <template v-else-if="errorMessage">
      <div class="empty-state error-state">
        <i class="mdi mdi-alert-circle-outline" />
        <span class="error-msg">{{ errorMessage }}</span>
      </div>
    </template>

    <!-- Empty / drop prompt -->
    <template v-else>
      <div class="empty-state">
        <i class="mdi mdi-image-filter-frames" />
        <span class="drop-hint">Drop phantom series</span>
      </div>
    </template>
  </div>
</template>

<style scoped>
.phantom-series-slot {
  position: relative;
  width: var(--small-thumbnail-width);
  height: var(--small-thumbnail-height);
  border: 1px solid var(--md-outline);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  overflow: hidden;
  background: var(--md-surface);
}

.phantom-series-slot.drag-over {
  border-color: var(--md-primary);
  background: rgba(var(--v-theme-primary), 0.08);
}

.phantom-series-slot.has-selection {
  border-style: solid;
  border-color: var(--md-success);
}

/* everything else stays the same */

.phantom-series-slot.has-error {
  border-color: var(--md-error);
}

.phantom-thumbnail {
  position: relative;
  width: 100%;
  height: 100%;
}

.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 6px;
}

.thumb-info {
  position: absolute;
  bottom: 2px;
  right: 4px;
  display: flex;
  gap: 4px;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
  pointer-events: none;
}

.series-idx {
  background: rgba(0, 0, 0, 0.55);
  padding: 1px 4px;
  border-radius: 3px;
}

.dims {
  background: rgba(0, 0, 0, 0.55);
  padding: 1px 4px;
  border-radius: 3px;
}

.clear-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.45);
  border: none;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    background 0.15s,
    color 0.15s;
}

.clear-btn:hover {
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  color: var(--md-on-surface-variant);
  pointer-events: none;
}

.empty-state i {
  font-size: 18px;
  opacity: 0.5;
}

.drop-hint {
  font-size: 9px;
  opacity: 0.6;
  text-align: center;
}

.error-state {
  color: var(--md-error);
}

.error-state i {
  opacity: 0.8;
}

.error-msg {
  font-size: 8px;
  text-align: center;
  max-width: 90px;
  line-height: 1.2;
}
</style>
